import React from 'react';
import { motion } from 'framer-motion';
import {
  Zap,
  Github,
  Linkedin,
  Mail,
  ArrowRight
} from 'lucide-react';
import { openCalendly } from '../lib/calendly';

const Footer: React.FC = () => {

  const footerLinks = {
    platform: [
      { name: "Capabilities", href: "#capabilities" },
      { name: "AI Operations", href: "#ai-operations" },
      { name: "How It Works", href: "#workflow" },
      { name: "Why DeploAI", href: "#why" }
    ],
    resources: [
      { name: "Trust & Security", href: "#trust" },
      { name: "FAQ", href: "#faq" }
    ]
  };

  const contactEmail = "info@tprimesolution.com";

  const socialLinks = [
    { icon: Github, href: "https://github.com/deploai", label: "GitHub" },
    { icon: Linkedin, href: "https://linkedin.com/company/deploai", label: "LinkedIn" }
  ];

  return (
    <footer className="relative overflow-hidden bg-dark-800 pt-16">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-neon-purple/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10">
        {/* CTA Section */}
        <div className="border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="inline-flex items-center space-x-2 glass rounded-full px-6 py-3 mb-8"
              >
                <Zap className="w-5 h-5 text-neon-blue" />
                <span className="text-sm font-medium text-gray-300">
                  Ready to get started?
                </span>
              </motion.div>
              
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-4xl md:text-5xl font-semibold mb-6"
              >
                <span className="text-white">Talk to us about</span>
                <br />
                <span className="gradient-text">your infrastructure</span>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                viewport={{ once: true }}
                className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto"
              >
                DeploAI is under active development. Get in touch to see current capabilities and discuss early access.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                viewport={{ once: true }}
                className="flex items-center justify-center"
              >
                <button
                  onClick={() => openCalendly('trial')}
                  className="premium-button text-lg px-8 py-4 flex items-center space-x-2 group"
                >
                  <span>Get Started</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
                </button>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Main Footer */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-neon-blue rounded-xl flex items-center justify-center neon-glow">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-semibold gradient-text">DeploAI</span>
              </div>
              
              <p className="text-gray-400 mb-6 max-w-md">
                An AI-powered cloud and DevOps platform being built to help engineering teams
                deploy, operate, secure, monitor, and troubleshoot infrastructure from one place.
              </p>

              <a
                href={`mailto:${contactEmail}`}
                className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors duration-200 mb-6 w-fit"
              >
                <Mail className="w-4 h-4" />
                <span>{contactEmail}</span>
              </a>

              <div className="flex space-x-4">
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="w-10 h-10 glass rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-200"
                  >
                    <social.icon className="w-5 h-5" />
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Platform Links */}
            <div>
              <h3 className="text-white font-semibold mb-4">Platform</h3>
              <ul className="space-y-3">
                {footerLinks.platform.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      className="text-gray-400 hover:text-white transition-colors duration-200"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Resources Links */}
            <div>
              <h3 className="text-white font-semibold mb-4">Resources</h3>
              <ul className="space-y-3">
                {footerLinks.resources.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      className="text-gray-400 hover:text-white transition-colors duration-200"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} DeploAI. All rights reserved.
            </p>
            
            <div className="flex items-center space-x-6 mt-4 md:mt-0">
              <span className="text-gray-400 text-sm">Made with ❤️ for DevOps teams</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 